import { z } from 'zod';

// User schemas
export const userSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  isAdmin: z.boolean().default(false),
  emailVerified: z.boolean().default(true),
  competeStudioId: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const signupSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Reset token is required'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

// Admin user management schemas
export const createUserSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  isAdmin: z.boolean().default(false),
  emailVerified: z.boolean().default(false),
});

export const updateUserSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').optional(),
  email: z.string().email('Invalid email address').optional(),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  isAdmin: z.boolean().optional(),
});

export const adminUserResponseSchema = z.object({
  success: z.boolean(),
  data: userSchema,
  message: z.string().optional(),
});

export const adminUsersListResponseSchema = z.object({
  success: z.boolean(),
  data: z.array(userSchema),
});

// User details schema for comprehensive user information
export const userDetailsSchema = z.object({
  user: userSchema,
  auth: z.object({
    accountCreated: z.string().datetime(),
    profileLastUpdated: z.string().datetime(),
    isAdmin: z.boolean(),
    emailVerified: z.boolean(),
    accountStatus: z.string(),
  }),
  statistics: z.object({
    totalSites: z.number(),
    publishedSites: z.number(),
    totalPages: z.number(),
    totalMedia: z.number(),
    totalMediaSizeMB: z.number(),
    totalSharedComponents: z.number(),
    totalPageTemplates: z.number(),
    totalSiteTemplates: z.number(),
    accountAge: z.number(),
  }),
  websites: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      subdomain: z.string(),
      customDomain: z.string().nullable(),
      isPublished: z.boolean(),
      description: z.string().nullable(),
      themeSettings: z.string().nullable(),
      createdAt: z.string().datetime(),
      updatedAt: z.string().datetime(),
      statistics: z.object({
        totalPages: z.number(),
        hasHomePage: z.boolean(),
        lastPageUpdated: z.number(),
      }),
    })
  ),
  media: z.object({
    totalFiles: z.number(),
    totalSizeBytes: z.number(),
    fileTypes: z.array(z.string()),
    byAssetType: z.record(z.number()),
    recentUploads: z.array(
      z.object({
        id: z.string().uuid(),
        filename: z.string(),
        mimeType: z.string(),
        size: z.number(),
        createdAt: z.string().datetime(),
      })
    ),
  }),
  components: z.object({
    totalComponents: z.number(),
    publicComponents: z.number(),
    privateComponents: z.number(),
    byType: z.record(z.number()),
    recentComponents: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        type: z.string(),
        isPublic: z.boolean(),
        createdAt: z.string().datetime(),
      })
    ),
  }),
  pageTemplates: z.object({
    totalTemplates: z.number(),
    publicTemplates: z.number(),
    totalUsageCount: z.number(),
    mostUsedTemplate: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
        usageCount: z.number(),
        isPublic: z.boolean(),
        createdAt: z.string().datetime(),
      })
      .nullable(),
    recentTemplates: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        usageCount: z.number(),
        isPublic: z.boolean(),
        createdAt: z.string().datetime(),
      })
    ),
  }),
  siteTemplates: z.object({
    totalTemplates: z.number(),
    publicTemplates: z.number(),
    totalUsageCount: z.number(),
    mostUsedTemplate: z
      .object({
        id: z.string().uuid(),
        name: z.string(),
        usageCount: z.number(),
        isPublic: z.boolean(),
        createdAt: z.string().datetime(),
      })
      .nullable(),
    recentTemplates: z.array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        usageCount: z.number(),
        isPublic: z.boolean(),
        createdAt: z.string().datetime(),
      })
    ),
  }),
  recentActivity: z.array(
    z.object({
      type: z.string(),
      name: z.string(),
      date: z.string().datetime(),
    })
  ),
  insights: z.object({
    mostActiveMonth: z.string().nullable(),
    preferredContentTypes: z.array(z.string()),
    engagementLevel: z.string(),
    templateContributor: z.boolean(),
    avgPagesPerSite: z.number(),
  }),
  subscription: z
    .object({
      plan: z
        .object({
          id: z.string(),
          name: z.string(),
          displayName: z.string(),
          priceMonthly: z.number(),
          priceYearly: z.number(),
          maxSites: z.number(),
          maxPagesPerSite: z.number(),
          customDomain: z.boolean(),
          contactForms: z.boolean(),
          advancedComponents: z.boolean(),
          competeStudio: z.boolean(),
        })
        .nullable(),
      subscription: z
        .object({
          id: z.string(),
          stripeCustomerId: z.string().nullable(),
          stripeSubscriptionId: z.string().nullable(),
          status: z.string(),
          currentPeriodStart: z.string().datetime(),
          currentPeriodEnd: z.string().datetime(),
          canceledAt: z.string().datetime().nullable(),
          cancelAtPeriodEnd: z.boolean(),
          trialEnd: z.string().datetime().nullable(),
        })
        .nullable(),
      usage: z.object({
        sites: z.number(),
        pages: z.number(),
      }),
      limits: z.object({
        maxSites: z.number(),
        maxPagesPerSite: z.number(),
        canUseDomain: z.boolean(),
        canUseContactForms: z.boolean(),
        canUseAdvancedComponents: z.boolean(),
        canUseCompeteStudio: z.boolean(),
      }),
    })
    .optional(),
});

export const userDetailsResponseSchema = z.object({
  success: z.boolean(),
  data: userDetailsSchema,
});

// Type exports
export type User = z.infer<typeof userSchema>;
export type SignupRequest = z.infer<typeof signupSchema>;
export type LoginRequest = z.infer<typeof loginSchema>;
export type ForgotPasswordRequest = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordRequest = z.infer<typeof resetPasswordSchema>;
export type CreateUserRequest = z.infer<typeof createUserSchema>;
export type UpdateUserRequest = z.infer<typeof updateUserSchema>;
export type AdminUserResponse = z.infer<typeof adminUserResponseSchema>;
export type AdminUsersListResponse = z.infer<typeof adminUsersListResponseSchema>;
export type UserDetails = z.infer<typeof userDetailsSchema>;
export type UserDetailsResponse = z.infer<typeof userDetailsResponseSchema>;
